<template>
  <div class="max-w-4xl mx-auto space-y-4 md:space-y-8">
    <!-- Game Header Container -->
    <div :class="['border-4 transition-all duration-500 overflow-hidden', isEasy ? 'bg-amber-50/95 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
      <!-- Nitwit Name Header -->
      <div :class="['text-center py-2 md:py-3 border-b-4 transition-colors', isEasy ? 'bg-amber-100/50 border-amber-800/30' : 'bg-stone-800/50 border-slate-600/50']">
        <p :class="['text-[10px] md:text-xs font-serif-body font-bold uppercase tracking-widest mb-0.5', isEasy ? 'text-amber-900/60' : 'text-amber-400/60']">Nitwit</p>
        <h1 :class="['text-xl md:text-3xl font-serif-display font-bold truncate px-4', isEasy ? 'ink-text' : 'text-amber-100']">{{ playerName }}</h1>
      </div>

      <!-- Indicators Grid -->
      <div class="grid grid-cols-4 lg:flex lg:flex-row justify-between items-stretch">
        <!-- Time Section -->
        <div class="text-center relative px-1 md:px-4 flex flex-col justify-start pt-2 md:pt-6 md:min-w-[160px] min-h-[60px] md:min-h-[120px] border-r border-amber-700/20 lg:border-r-0">
          <p :class="['text-[10px] md:text-sm font-serif-body font-bold mb-0.5 md:mb-1', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Time</p>
          <p :class="['text-lg md:text-6xl font-serif-display font-bold transition-colors', timeLeft < 15 ? 'text-red-700 animate-pulse' : (isEasy ? 'text-green-700' : 'text-blue-400')]">
            {{ timeLeft }}<span class="text-[10px] md:text-2xl">s</span>
          </p>

          <!-- Floating Time Animation -->
          <transition name="pop-float">
            <div v-if="showTimeAnimation"
              :key="`time-${lastTimeChange}`"
              :class="['absolute -top-4 md:-top-6 left-1/2 -translate-x-1/2 font-serif-display font-black text-xl md:text-5xl pointer-events-none z-50', lastTimeChange > 0 ? 'text-emerald-400' : 'text-red-500']"
              :style="lastTimeChange > 0
                ? 'text-shadow: 0 0 10px #34d399, 0 0 20px #10b981, 2px 2px 0px rgba(0,0,0,0.5); filter: drop-shadow(0 0 5px rgba(52, 211, 153, 0.6));'
                : 'text-shadow: 0 0 10px #f87171, 0 0 20px #ef4444, 2px 2px 0px rgba(0,0,0,0.5); filter: drop-shadow(0 0 5px rgba(248, 113, 113, 0.6));'"
            >
              {{ lastTimeChange > 0 ? '+' : '' }}{{ lastTimeChange }}s
            </div>
          </transition>
        </div>

        <!-- Score & Streak Sections -->
        <div :class="['col-span-2 flex flex-col lg:border-x-4 transition-colors lg:flex-[2]', isEasy ? 'border-amber-700/40' : 'border-slate-600/50']">
          <div class="flex items-center flex-1">
            <div class="text-center px-1 md:px-4 border-inherit flex-1 flex flex-col justify-start pt-2 md:pt-6 min-h-[60px] md:min-h-[120px] relative">
              <p :class="['text-[10px] md:text-sm font-serif-body font-bold mb-0.5 md:mb-1', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Score</p>
              <p :class="['text-lg md:text-6xl font-serif-display font-bold transition-colors', isEasy ? 'text-amber-800' : 'text-amber-400']">{{ score }}</p>

              <!-- Floating Points Animation -->
              <transition name="pop-float">
                <div v-if="showPointsAnimation"
                  class="absolute -top-4 md:-top-6 inset-x-0 text-center font-serif-display font-black text-2xl md:text-5xl text-yellow-400 pointer-events-none z-50"
                  style="text-shadow: 0 0 10px #fbbf24, 0 0 20px #f59e0b, 2px 2px 0px rgba(0,0,0,0.5); filter: drop-shadow(0 0 5px rgba(251, 191, 36, 0.6));"
                >
                  +{{ lastPointsEarned }}
                </div>
              </transition>
            </div>
            <div class="text-center px-1 md:px-4 border-inherit flex-1 flex flex-col justify-start pt-2 md:pt-6 min-h-[60px] md:min-h-[120px]">
              <p :class="['text-[10px] md:text-sm font-serif-body font-bold mb-0.5 md:mb-1', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Streak</p>
              <p :class="['text-lg md:text-6xl font-serif-display font-bold transition-colors', isEasy ? 'text-amber-800' : 'text-amber-400']">{{ currentStreak }}</p>
            </div>
          </div>

          <div class="mb-1 md:mb-4 px-1 md:px-4 min-h-1 md:min-h-2">
            <streak-bonus-bar
                :streak-progress="streakProgress"
                :streak-bonus-percent="streakBonusPercent"
                :is-easy="isEasy"
            ></streak-bonus-bar>
          </div>
        </div>

        <!-- Passes Section -->
        <div :class="['text-center px-1 md:px-4 transition-colors flex flex-col justify-start pt-2 md:pt-6 md:min-w-[160px] min-h-[60px] md:min-h-[120px] lg:border-amber-700/40', isEasy ? 'border-amber-700/20' : 'border-slate-600/30']">
          <p :class="['text-[10px] md:text-sm font-serif-body font-bold mb-0.5 md:mb-1', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Passes</p>
          <div class="flex justify-center space-x-0.5 md:space-x-1 mt-1 md:mt-8">
            <div v-for="i in maxPassesDisplay" :key="i"
                 :class="['w-2 h-3 md:w-4 md:h-6 border-2 transition-all duration-300',
                i <= passesRemaining
                  ? (isEasy ? 'bg-blue-500 border-blue-800 shadow-md' : 'bg-blue-600 border-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.5)]')
                  : 'bg-transparent border-slate-500/30 opacity-30']"
              style="border-radius: 2px; transform: rotate(-5deg);"
            ></div>
          </div>
          <p v-if="passesRemaining === 0" class="text-[8px] md:text-xs uppercase font-bold text-red-500 mt-0.5 animate-pulse">None!</p>
        </div>
      </div>
    </div>

    <!-- Riddle Card -->
    <div v-if="currentRiddle" :class="['p-6 md:p-12 border-t-8 min-h-0 md:min-h-[450px] flex flex-col justify-between relative overflow-hidden transition-all duration-500', isEasy ? 'bg-amber-50/95 border-amber-700 sepia-shadow' : 'card-dark-rich border-amber-500 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
      <div class="p-4 md:p-6 flex flex-wrap md:flex-nowrap items-center justify-end gap-2 md:space-x-4">
        <button
          v-if="passesRemaining > 0"
          @click="passRiddle"
          :disabled="isSubmitting"
          :class="['text-[10px] md:text-xs font-serif-body font-bold transition-colors px-2 py-1 md:px-3 md:py-2 border-2', isEasy ? 'text-blue-800 bg-blue-50 border-blue-700/50 hover:bg-blue-100' : 'text-blue-300 bg-blue-900/20 border-blue-700/40 hover:border-blue-600']"
          style="border-radius: 6px; letter-spacing: 0.05em;"
        >
          Pass (-{{ GAME_CONFIG.PASS_PENALTY }}s)
        </button>
        <button
          @click="endGame"
          :class="['text-[10px] md:text-xs font-serif-body font-bold transition-colors px-2 py-1 md:px-3 md:py-2 border-2', isEasy ? 'text-red-800 bg-red-50 border-red-700/50 hover:bg-red-100' : 'text-red-300 bg-red-900/20 border-red-700/40 hover:border-red-600']"
          style="border-radius: 6px; letter-spacing: 0.05em;"
        >
          End Game
        </button>
        <span :class="['text-[10px] md:text-xs px-2 py-1 md:px-4 md:py-2 font-serif-body font-bold border-2 transition-colors cursor-default', isEasy ? 'bg-amber-100 text-amber-900 border-amber-700/50' : 'bg-amber-900/20 text-amber-300 border-amber-700/50']" style="border-radius: 20px; letter-spacing: 0.05em;">
          {{ currentRiddle.category || 'General' }}
        </span>
      </div>

      <div class="mb-8 md:mb-12 mt-4 md:mt-8 flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 text-center md:text-left">
        <div class="flex-shrink-0 w-full md:w-auto flex justify-center md:block overflow-visible">
          <Mascot :size="isEasy ? 'w-36 h-36 md:w-48 md:h-48' : 'w-24 h-24 md:w-32 md:h-32'" :mood="mascotMood" :message="hintMessage" />
        </div>
        <h2 :class="['text-2xl md:text-5xl font-serif-display leading-tight italic text-shadow-warm transition-colors hanging-quotes', isEasy ? 'ink-text' : 'text-amber-100']">
          “{{ currentRiddle.question }}”
        </h2>
      </div>

      <div class="space-y-4 md:space-y-6">
        <div v-if="gameMode === 'easy' && currentRiddle.options" class="relative">
          <div v-if="cooldownActive" class="absolute -top-4 left-0 right-0 h-1.5 bg-stone-800/50 rounded-full overflow-hidden z-10">
            <div 
              class="h-full bg-amber-500 rounded-full transition-[width] ease-linear"
              :class="cooldownShrinking ? 'w-0' : 'w-full'"
              :style="{ transitionDuration: `${cooldownDuration}ms` }"
            ></div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <button
              v-for="option in currentRiddle.options"
              :key="option"
              @click="submitAnswer(option)"
              :disabled="isSubmitting"
              :class="[
                'p-4 md:p-6 border-3 text-lg md:text-2xl font-serif-body font-semibold transition-all disabled:opacity-100 hand-drawn-shadow',
                !isSubmitting ? (isEasy ? 'bg-amber-50 border-amber-700/40 ink-text hover:border-amber-800 hover:bg-white' : 'bg-stone-800 border-amber-700/40 text-amber-100 hover:border-amber-600 hover:bg-stone-700/50') : '',
                isSubmitting && option === selectedAnswer && feedback?.correct ? 'bg-green-600 border-green-800 text-white' : '',
                isSubmitting && option === selectedAnswer && !feedback?.correct ? 'bg-red-600 border-red-800 text-white' : '',
                isSubmitting && option === correctAnswer ? 'bg-green-600 border-green-800 text-white' : '',
                isSubmitting && option !== selectedAnswer && option !== correctAnswer ? (isEasy ? 'bg-stone-50 border-stone-200 text-stone-400 opacity-50' : 'bg-stone-800/50 border-stone-800 text-stone-600 opacity-50') : ''
              ]"
              style="border-radius: 6px;"
            >
              {{ option }}
            </button>
          </div>
          <div v-if="feedback" :class="['absolute -top-20 md:-top-16 left-0 right-0 p-3 md:p-4 text-center font-serif-display font-bold text-xl md:text-3xl transition-all animate-bounce sepia-shadow border-3', feedback.correct ? 'text-green-700 bg-green-100/90 border-green-700/50' : 'text-red-700 bg-red-100/90 border-red-700/50']" style="border-radius: 8px;">
            {{ feedback.message }}
            <span v-if="!feedback.correct && correctAnswer" class="block text-sm md:text-lg mt-1 font-serif-body font-semibold" style="letter-spacing: 0.05em;">
              The answer was: {{ correctAnswer }}
            </span>
          </div>
        </div>

        <div v-else class="space-y-4 md:space-y-6">
          <div class="relative">
            <!-- Cooldown Progress Bar for Blitz/Hard Mode -->
            <div v-if="cooldownActive" class="absolute -top-4 left-0 right-0 h-1.5 bg-stone-800/50 rounded-full overflow-hidden z-10">
              <div 
                class="h-full bg-amber-500 rounded-full transition-[width] ease-linear"
                :class="cooldownShrinking ? 'w-0' : 'w-full'"
                :style="{ transitionDuration: `${cooldownDuration}ms` }"
              ></div>
            </div>
            <input
              v-model="answer"
              @keyup.enter="submitAnswer()"
              ref="answerInput"
              type="text"
              name="riddle-response"
              placeholder="Type your answer..."
              autocomplete="off"
              data-lpignore="true"
              data-form-type="other"
              class="w-full text-xl md:text-3xl font-serif-body p-4 md:p-6 bg-stone-800/90 border-3 border-amber-700/50 focus:border-amber-600 focus:outline-none transition-all text-amber-50 placeholder-stone-500 hand-drawn-shadow"
              style="border-radius: 6px;"
              :disabled="isSubmitting"
              aria-label="Your riddle answer"
            />
            <div v-if="feedback" :class="['absolute -top-10 md:-top-12 left-0 right-0 p-2 text-center font-serif-display font-bold text-lg md:text-xl transition-all animate-bounce', feedback.correct ? 'text-green-700' : 'text-red-700']">
              {{ feedback.message }}
            </div>
          </div>

          <button
            @click="submitAnswer()"
            :disabled="isSubmitting"
            class="w-full bg-amber-700 hover:bg-amber-500 text-white font-serif-display font-bold text-xl md:text-2xl py-4 md:py-6 transition-all disabled:opacity-50 hand-drawn-shadow border-4 border-amber-900"
            style="border-radius: 8px; letter-spacing: 0.05em;"
            aria-label="Submit answer"
          >
            {{ isSubmitting ? 'Checking...' : 'Submit Answer' }}
          </button>
        </div>
      </div>
    </div>

    <div v-else :class="['text-center p-20 border-4 transition-all duration-500', isEasy ? 'bg-amber-50/95 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
      <div class="relative w-24 h-24 mx-auto mb-8">
        <div :class="['absolute inset-0 border-4 rounded-full', isEasy ? 'border-amber-700/20' : 'border-amber-700/20']"></div>
        <div :class="['absolute inset-0 border-4 border-t-transparent rounded-full animate-spin', isEasy ? 'border-amber-700' : 'border-amber-600']"></div>
      </div>
      <p :class="['text-2xl font-serif-body font-semibold animate-pulse', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Summoning a riddle...</p>
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import apiClient from '../api'
import {GAME_CONFIG} from '../config'
import Mascot from './Mascot.vue'
import StreakBonusBar from './StreakBonusBar.vue'
import {trackRiddleGame} from '../analytics'

const props = defineProps({
  playerName: {
    type: String,
    required: true
  },
  gameMode: {
    type: String,
    default: 'blitz'
  },
  initialPasses: {
    type: Number,
    default: null // null means use GAME_CONFIG.MAX_PASSES
  }
})

const emit = defineEmits(['game-over', 'update-score'])

const isEasy = computed(() => props.gameMode === 'easy')
const mascotMood = ref('thinking')

// Max passes to display (either initial passes or config max, whichever is higher)
const maxPassesDisplay = computed(() => {
  return Math.max(props.initialPasses ?? GAME_CONFIG.MAX_PASSES, GAME_CONFIG.MAX_PASSES)
})

const streakBonusPercent = computed(() => {
  return Math.min(currentStreak.value * GAME_CONFIG.SCORING.STREAK_BONUS_STEP, GAME_CONFIG.SCORING.MAX_STREAK_BONUS)
})

const streakProgress = computed(() => {
  return (streakBonusPercent.value / GAME_CONFIG.SCORING.MAX_STREAK_BONUS) * 100
})

const sessionId = ref(null)
const currentRiddle = ref(null)
const answer = ref('')
const timeLeft = ref(GAME_CONFIG.INITIAL_TIME)
const score = ref(0)
const currentStreak = ref(0)
// Use initialPasses prop if provided, otherwise fall back to config
const passesRemaining = ref(props.initialPasses ?? GAME_CONFIG.MAX_PASSES)
const passedRiddles = ref([])
const isSubmitting = ref(false)
const cooldownActive = ref(false)
const cooldownShrinking = ref(false)
const cooldownDuration = ref(0)
const feedback = ref(null)
const lastPointsEarned = ref(0)
const showPointsAnimation = ref(false)
const lastTimeChange = ref(0)
const showTimeAnimation = ref(false)
const selectedAnswer = ref(null)
const correctAnswer = ref(null)
const riddleStartTime = ref(0)
const answerInput = ref(null)
const hintMessage = ref('')
const wrongAnswersCount = ref(0)
const masteredRiddles = ref(false)
let hintTimer = null

watch(() => feedback.value, (newFeedback) => {
  if (newFeedback) {
    mascotMood.value = newFeedback.correct ? 'happy' : 'sad'
  } else {
    mascotMood.value = 'thinking'
  }
})

let timer = null

const startGame = async () => {
  try {
    const res = await apiClient.post('/game/start/', {
      player_name: props.playerName,
      game_mode: props.gameMode
    })
    sessionId.value = res.data.session_id
    fetchRiddle()
    startTimer()
  } catch (error) {
    console.error('Failed to start game:', error)
  }
}

const fetchRiddle = async (preserveFeedback = false) => {
  try {
    const res = await apiClient.get(`/game/${sessionId.value}/riddle/`)
    currentRiddle.value = res.data
    answer.value = ''
    selectedAnswer.value = null
    correctAnswer.value = null
    riddleStartTime.value = Date.now()
    wrongAnswersCount.value = 0
    if (!preserveFeedback) {
      feedback.value = null
    }
    hintMessage.value = ''
    clearTimeout(hintTimer)
    startHintTimer()
    nextTick(() => answerInput.value?.focus())
  } catch (error) {
    console.error('Failed to fetch riddle:', error)
    if (error.response?.status === 404) {
      endGame()
    } else {
      feedback.value = { correct: false, message: 'Connection error! Retrying...' }
      setTimeout(fetchRiddle, 3000)
    }
  }
}

const startCooldown = (duration) => {
  cooldownActive.value = true
  cooldownShrinking.value = false
  cooldownDuration.value = duration
  
  // Use a small delay to ensure the browser has rendered the 100% width state
  // before we start the transition to 0%
  setTimeout(() => {
    cooldownShrinking.value = true
  }, 50)
}

const submitAnswer = async (selectedOption = null) => {
  const answerValue = selectedOption || answer.value
  if (!answerValue.trim() || isSubmitting.value) return

  // Handle slash commands
  const trimmedAnswer = answerValue.trim().toLowerCase()
  if (trimmedAnswer === '/pass') {
    answer.value = ''
    if (passesRemaining.value > 0) {
      passRiddle()
    }
    return
  }
  if (trimmedAnswer === '/end') {
    answer.value = ''
    endGame()
    return
  }

  clearTimeout(hintTimer)
  isSubmitting.value = true
  selectedAnswer.value = answerValue
  const responseTime = (Date.now() - riddleStartTime.value) / 1000

  try {
    const res = await apiClient.post(`/game/${sessionId.value}/answer/`, {
      riddle_id: currentRiddle.value.id,
      answer: answerValue,
      response_time: responseTime,
      time_left: timeLeft.value
    })

    if (res.data.is_correct) {
      currentStreak.value = res.data.current_streak
      score.value = res.data.current_score
      const pointsEarned = res.data.points_earned
      lastPointsEarned.value = pointsEarned

      lastTimeChange.value = GAME_CONFIG.CORRECT_ANSWER_BONUS

      // Track correct answer
      trackRiddleGame.answerRiddle(true, currentRiddle.value.difficulty, timeLeft.value)

      // Trigger animations
      showPointsAnimation.value = false
      showTimeAnimation.value = false
      setTimeout(() => {
        showPointsAnimation.value = true
        showTimeAnimation.value = true
        setTimeout(() => {
          showPointsAnimation.value = false
          showTimeAnimation.value = false
        }, 1400)
      }, 50)

      timeLeft.value = Math.min(GAME_CONFIG.MAX_TIME_CAP, timeLeft.value + GAME_CONFIG.CORRECT_ANSWER_BONUS)
      feedback.value = { correct: true, message: `Excellent! +${pointsEarned} pts (+${GAME_CONFIG.CORRECT_ANSWER_BONUS}s)` }
      emit('update-score', score.value)
      
      // Check if player has mastered all riddles
      if (res.data.mastered_riddles) {
        masteredRiddles.value = true
        hintMessage.value = "You are indeed a Master of Riddles! I am out of riddles for now -- give me a moment maybe I can think of some more."
        mascotMood.value = 'happy'

        // Brief delay for mascot message, then end game (no cooldown bar)
        setTimeout(() => {
          endGame()
          isSubmitting.value = false
        }, 2000)
      } else {
        // Fetch next riddle immediately, but let feedback message persist
        isSubmitting.value = false
        if (timeLeft.value > 0) {
          fetchRiddle(true) // true = preserve feedback
        } else {
          endGame()
        }
        // Clear feedback after delay so user can read it
        setTimeout(() => {
          feedback.value = null
        }, 1200)
      }
    } else {
      currentStreak.value = 0
      wrongAnswersCount.value++
      const cooldownPenalty = GAME_CONFIG.WRONG_ANSWER_PENALTIES[wrongAnswersCount.value - 1] || GAME_CONFIG.PASS_PENALTY

      // Track incorrect answer
      trackRiddleGame.answerRiddle(false, currentRiddle.value.difficulty, timeLeft.value)

      // Update mascot mood for mistakes
      mascotMood.value = 'sad'

      if (wrongAnswersCount.value >= GAME_CONFIG.MAX_WRONG_ANSWERS && passesRemaining.value > 0) {
        // Auto-pass: apply pass penalty since we're consuming a pass token
        const passPenalty = GAME_CONFIG.PASS_PENALTY

        lastTimeChange.value = -passPenalty
        showTimeAnimation.value = false
        setTimeout(() => {
          showTimeAnimation.value = true
          setTimeout(() => { showTimeAnimation.value = false }, 1400)
        }, 50)

        timeLeft.value = Math.max(0, timeLeft.value - passPenalty)

        feedback.value = { correct: false, message: `Too many attempts! Using a pass. -${passPenalty}s` }
        correctAnswer.value = res.data.correct_answer
        passedRiddles.value.push({
          question: currentRiddle.value.question,
          answer: correctAnswer.value
        })
        passesRemaining.value = Math.max(0, passesRemaining.value - 1)

        startCooldown(2000)

        setTimeout(() => {
          if (timeLeft.value > 0) {
            fetchRiddle()
          } else {
            endGame()
          }
          isSubmitting.value = false
          cooldownActive.value = false
          cooldownShrinking.value = false
        }, 2000)
      } else {
        // Wrong answer: no time penalty, use cooldown instead
        const cooldownMs = cooldownPenalty * 1000

        let message = `Incorrect! Try again.`
        if (passesRemaining.value === 0) {
          message = `Incorrect! No passes left—you must solve this one!`
        }

        feedback.value = { correct: false, message }
        startCooldown(cooldownMs)
        setTimeout(() => {
          feedback.value = null
          isSubmitting.value = false
          cooldownActive.value = false
          cooldownShrinking.value = false
          if (wrongAnswersCount.value < GAME_CONFIG.MAX_WRONG_ANSWERS) {
            answer.value = ''
            selectedAnswer.value = null
          }
          mascotMood.value = 'thinking'
          startHintTimer()
          nextTick(() => answerInput.value?.focus())
        }, cooldownMs)
      }
    }

  } catch (error) {
    console.error('Failed to submit answer:', error)
    isSubmitting.value = false
  }
}

const passRiddle = async () => {
  if (passesRemaining.value <= 0 || isSubmitting.value || !currentRiddle.value) return
  
  currentStreak.value = 0
  clearTimeout(hintTimer)
  isSubmitting.value = true
  const responseTime = (Date.now() - riddleStartTime.value) / 1000
  
  try {
    const res = await apiClient.post(`/game/${sessionId.value}/answer/`, {
      riddle_id: currentRiddle.value.id,
      is_pass: true,
      response_time: responseTime,
      time_left: timeLeft.value
    })
    const responseData = res.data

    // Track pass event
    trackRiddleGame.pass()

    lastTimeChange.value = -GAME_CONFIG.PASS_PENALTY
    showTimeAnimation.value = false
    setTimeout(() => {
      showTimeAnimation.value = true
      setTimeout(() => { showTimeAnimation.value = false }, 1400)
    }, 50)

    passesRemaining.value--
    passedRiddles.value.push({
      question: currentRiddle.value.question,
      answer: responseData.correct_answer
    })
    timeLeft.value = Math.max(0, timeLeft.value - GAME_CONFIG.PASS_PENALTY)
    feedback.value = { correct: false, message: `Passed! -${GAME_CONFIG.PASS_PENALTY}s` }
    
    startCooldown(1000)

    setTimeout(() => {
      if (timeLeft.value > 0) {
        fetchRiddle()
      } else {
        endGame()
      }
      isSubmitting.value = false
      cooldownActive.value = false
      cooldownShrinking.value = false
    }, 1000)
  } catch (error) {
    console.error('Failed to pass riddle:', error)
    isSubmitting.value = false
  }
}

const startTimer = () => {
  timer = setInterval(() => {
    timeLeft.value--
    if (timeLeft.value <= 0) {
      endGame()
    }
  }, 1000)
}

const startHintTimer = () => {
  hintTimer = setTimeout(() => {
    if (currentRiddle.value) {
      if (passesRemaining.value === 0) {
        hintMessage.value = "You're out of passes! You have to solve this one to move on."
        return
      }
      const distractors = currentRiddle.value.distractors 
        ? currentRiddle.value.distractors.split(',').map(d => d.trim())
        : (currentRiddle.value.options ? currentRiddle.value.options.filter(o => o !== currentRiddle.value.answer) : [])
      
      if (distractors.length > 0) {
        const randomDistraction = distractors[Math.floor(Math.random() * distractors.length)].toLowerCase()
        const variations = [
          `I know what you're thinking, but it's not ${randomDistraction}.`,
          `Thinking of ${randomDistraction}? Guess again!`,
          `It might seem like ${randomDistraction}, but it's not.`,
        ]
        hintMessage.value = variations[Math.floor(Math.random() * variations.length)]
      }
    }
  }, GAME_CONFIG.HINT_TIMEOUT * 1000)
}

const endGame = async () => {
  clearInterval(timer)
  clearTimeout(hintTimer)
  try {
    const res = await apiClient.post(`/game/${sessionId.value}/end/`)
    emit('game-over', {
      score: res.data.final_score,
      duration: res.data.duration,
      rank: res.data.rank,
      playerName: res.data.player_name,
      sessionId: res.data.session_id,
      passedRiddles: passedRiddles.value,
      masteredRiddles: res.data.mastered_riddles,
      perfectGame: res.data.perfect_game
    })
  } catch (error) {
    console.error('Failed to end game:', error)
    emit('game-over', { score: score.value, duration: 0, masteredRiddles: false, perfectGame: false })
  }
}

onMounted(startGame)
onUnmounted(() => clearInterval(timer))
</script>

<style scoped>
.pop-float-enter-active {
  animation: pop-and-float 1.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

@keyframes pop-and-float {
  0% {
    transform: scale(0.5) translateY(20px) rotate(-15deg);
    opacity: 0;
  }
  20% {
    transform: scale(1.3) translateY(0) rotate(10deg);
    opacity: 1;
  }
  40% {
    transform: scale(1) translateY(-10px) rotate(-5deg);
  }
  100% {
    transform: scale(0.9) translateY(-60px) rotate(5deg);
    opacity: 0;
  }
}

.sepia-shadow {
  box-shadow: 4px 4px 0px rgba(120, 80, 40, 0.2);
}

.card-dark-rich {
  background: radial-gradient(circle at top left, #2d2a28, #1a1817);
}

.ink-text {
  color: #2c1810;
}

.hand-drawn-shadow {
  box-shadow: 3px 3px 0px rgba(0,0,0,0.15);
}

.hanging-quotes {
  text-indent: -0.45em;
  padding-left: 0.45em;
}

@media (max-width: 767px) {
  .hanging-quotes {
    text-indent: 0;
    padding-left: 0;
  }
}

</style>
